import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // Import useNavigate for navigation
import { getCardRarityClass } from '../utils/cardUtils';
import { sortTeamLineup } from '../helpers/teamHelpers';
import { getVariationAbbrev } from '../helpers/cardHelpers';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../auth/AuthAtoms';
import { patchUserTeam } from '../api/userTeams';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

const TeamList = ({ teams, onRefetch }) => {
    const [currentUser] = useAtom(currentUserAtom);
    const [expandedTeamID, setExpandedTeamID] = useState(null);
    const [showHidden, setShowHidden] = useState(false);
    const navigate = useNavigate(); // Use navigate for navigation to the edit page

    const toggleHide = useCallback(async (teamID, currentlyHidden) => {
        try {
            await patchUserTeam(teamID, { isHidden: !currentlyHidden });
            await onRefetch();
        } catch {
            toast.error('Failed to update team visibility');
        }
    }, [onRefetch]);

    const toggleTeam = useCallback(teamID => {
        setExpandedTeamID(prev => prev === teamID ? null : teamID);
    }, []);

    const handleEdit = useCallback(team => {
        navigate('/team/edit', { state: { team } });
    }, [navigate]);

    const visibleTeams = useMemo(() => teams.filter(t => !t.isHidden), [teams]);
    const hiddenTeams = useMemo(() => teams.filter(t => t.isHidden), [teams]);

    const renderTeamCard = team => (
        <div key={team.userTeamID} className="team-card">
            <div className="team-header" onClick={() => toggleTeam(team.userTeamID)}>
                <span className="team-name">{team.name}</span>
                <span className="team-score">Score: {team.score}</span>
            </div>
            <div className={`team-lineup ${expandedTeamID === team.userTeamID ? 'expanded' : ''}`}>
                <span>Lineup</span>
                {sortTeamLineup(team.UserTeamLineups).map(lineup => (
                    <div key={lineup.lineupID} className="lineup-row">
                        {lineup.lineupOrder && (
                            <span className="batting-order bold"> {lineup.lineupOrder} </span>
                        )}
                        <span className="position"> {lineup.position} </span>
                        <Link to={`/cards/${lineup.Card.cardID}`}>
                            <span className={`rarity-dot ${getCardRarityClass(lineup.Card.rating)}`}></span>
                            ({lineup.Card.rating}) {lineup.Card.year} {lineup.Card.name}
                            {lineup.Card.customImage && (
                                <span className="variation-label">ART</span>
                            )}
                            {getVariationAbbrev(lineup.Card) && (
                                <span className="variation-label">{getVariationAbbrev(lineup.Card)}</span>
                            )}
                        </Link>
                    </div>
                ))}
            </div>
            {/* Check if the user is the team owner or isAdmin to show the Edit button */}
            {(team.userID === currentUser.userID || currentUser.isAdmin) && (
                <div className="team-actions">
                    <button className="edit-button" onClick={() => handleEdit(team)}>
                        Edit
                    </button>
                    <button className="boring_button hide-button" onClick={() => toggleHide(team.userTeamID, team.isHidden)}>
                        {team.isHidden ? 'Unhide' : 'Hide'}
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <div className="team-list-wrapper">
            {teams.length === 0 ? (
                <p>No teams found.</p>
            ) : (
                <>
                    <div className="team-list">
                        {visibleTeams.map(renderTeamCard)}
                    </div>
                    {hiddenTeams.length > 0 && (
                        <>
                            <label className="show-hidden-toggle">
                                <input
                                    type="checkbox"
                                    checked={showHidden}
                                    onChange={() => setShowHidden(v => !v)}
                                />
                                {' '}Show hidden teams ({hiddenTeams.length})
                            </label>
                            {showHidden && (
                                <div className="team-list hidden-team-list">
                                    {hiddenTeams.map(renderTeamCard)}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

TeamList.propTypes = {
    onRefetch: PropTypes.func.isRequired,
    teams: PropTypes.arrayOf(
        PropTypes.shape({
            userTeamID: PropTypes.any.isRequired,
            name: PropTypes.string.isRequired,
            score: PropTypes.number.isRequired,
            isHidden: PropTypes.bool,
            UserTeamLineups: PropTypes.arrayOf(
                PropTypes.shape({
                    lineupID: PropTypes.any.isRequired,
                    position: PropTypes.string.isRequired,
                    Card: PropTypes.shape({
                        cardID: PropTypes.any.isRequired,
                        rating: PropTypes.number.isRequired,
                        year: PropTypes.number.isRequired,
                        name: PropTypes.string.isRequired,
                    }).isRequired,
                    lineupOrder: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
                })
            ).isRequired,
        })
    ).isRequired,
};

export default TeamList;
